import { t } from '@lingui/core/macro';
import {
    CustomPartCategoryDetailsType,
    CustomPartSpecificationLiteDto,
    FullPart,
    isCustomComponentFull,
    isCustomFullPart,
    isGenericFullPart,
    isOtsComponentFull,
    isOtsFullPart,
    isPcbCustomPartSpecification,
    PartLite,
    PartLiteTypes,
} from '@luminovo/api-contracts';
import { assertUnreachable, throwErrorUnlessProduction } from '@luminovo/commons';
import { colorSystem, CopyToClipboardTooltip, Flexbox, Tag, Text } from '@luminovo/design-system';
import { transEnum } from '@luminovo/frontend-utils';

import { formatPart, formatPartLite } from '../../formatters';
import { getTranslatedCustomPartCategoryName, partSpecificationTypeTranslations } from '../../i18n';

export const LabelPart: React.FunctionComponent<{
    part: FullPart | undefined;
    assemblyNameForCustomPart?: string;
    enableCopyToClipboard?: boolean;
}> = ({ part, enableCopyToClipboard = false, assemblyNameForCustomPart: assemblyName }): JSX.Element => {
    if (!part) {
        return <></>;
    }

    if (isOtsFullPart(part)) {
        return <>{formatPart(part)}</>;
    }

    if (isOtsComponentFull(part) || isCustomComponentFull(part)) {
        return (
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Tag attention={'low'} color={'blue'} label={t`IPN`} />
                <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                    {part.id}
                </Text>
            </Flexbox>
        );
    }

    if (isGenericFullPart(part)) {
        return (
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Tag attention={'low'} color={'green'} label={t`Generic`} />
                <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                    {transEnum(part.content.type, partSpecificationTypeTranslations)}
                </Text>
            </Flexbox>
        );
    }

    if (isCustomFullPart(part)) {
        if (isPcbCustomPartSpecification(part)) {
            return (
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'baseline',
                    }}
                >
                    <Tag attention={'low'} color={'green'} label={t`PCB`} />
                    {assemblyName && (
                        <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                            {assemblyName}
                        </Text>
                    )}
                </Flexbox>
            );
        }

        return (
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'baseline',
                }}
            >
                <Tag attention={'low'} color={'blue'} label={t`Custom`} />{' '}
                <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                    {getTranslatedCustomPartCategoryName(part.category)}
                </Text>{' '}
                <CopyToClipboardTooltip
                    value={enableCopyToClipboard && part.description ? part.description : undefined}
                >
                    <Text
                        variant="body-small"
                        color={colorSystem.neutral[6]}
                        style={{ whiteSpace: 'nowrap' }}
                    >{`${part.description}`}</Text>
                </CopyToClipboardTooltip>
            </Flexbox>
        );
    }

    assertUnreachable(part);
};

export const LabelPartLite = ({
    part,
    pcbAssemblyName,
    enableCopyToClipboard = false,
}: {
    part: PartLite;
    pcbAssemblyName?: string;
    enableCopyToClipboard?: boolean;
}): JSX.Element => {
    const kind = part.kind;
    switch (kind) {
        case PartLiteTypes.OffTheShelf:
            return <>{formatPartLite(part)}</>;
        case PartLiteTypes.Generic:
            return (
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'baseline',
                    }}
                >
                    <Tag attention={'low'} color={'green'} label={t`Generic`} />
                    <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                        {transEnum(part.content.type, partSpecificationTypeTranslations)}
                    </Text>
                </Flexbox>
            );
        case PartLiteTypes.Ipn:
        case PartLiteTypes.CustomComponent:
            return (
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'baseline',
                    }}
                >
                    <Tag attention={'low'} color={'blue'} label={t`IPN`} />
                    <Text variant="inherit" color={'inherit'} style={{ whiteSpace: 'nowrap' }}>
                        {part.id}
                    </Text>
                </Flexbox>
            );
        case PartLiteTypes.Custom: {
            if (part.category_details.type === CustomPartCategoryDetailsType.PCB) {
                return (
                    <Flexbox
                        gap={4}
                        sx={{
                            alignItems: 'baseline',
                        }}
                    >
                        <Tag attention={'low'} color={'green'} label={t`PCB`} />
                        <Text variant="inherit" color={'inherit'} showEllipsis={'ellipsisOnly'}>
                            {formatCustomPartLiteDetail(part)}
                        </Text>
                        {pcbAssemblyName && (
                            <Text variant="inherit" color={'inherit'} showEllipsis={'ellipsisOnly'}>
                                {pcbAssemblyName}
                            </Text>
                        )}
                    </Flexbox>
                );
            }
            return (
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'baseline',
                    }}
                >
                    <Tag attention={'low'} color={'blue'} label={t`Custom`} />
                    <Text variant="inherit" color={'inherit'} showEllipsis={'ellipsisOnly'}>
                        {formatCustomPartLiteDetail(part)}
                    </Text>
                    {part.description && (
                        <CopyToClipboardTooltip
                            value={enableCopyToClipboard && part.description ? part.description : undefined}
                        >
                            <Text
                                variant="body-small"
                                color={colorSystem.neutral[6]}
                                showEllipsis={'ellipsisOnly'}
                            >{`${part.description}`}</Text>
                        </CopyToClipboardTooltip>
                    )}
                </Flexbox>
            );
        }
        case PartLiteTypes.RawSpecification:
        case PartLiteTypes.Unknown:
            // Unknown should not be displayed as a label
            throwErrorUnlessProduction(new Error(`Encountered part lite of type "Unknown": ${JSON.stringify(part)}`));
            return <>{formatPartLite(part)}</>;
        default:
            assertUnreachable(kind);
    }
};

function formatCustomPartLiteDetail(part: CustomPartSpecificationLiteDto): string {
    if (part.drawing_number) {
        return t`Drawing: ${part.drawing_number}${part.revision ? ` • ${part.revision}` : ''}`;
    }
    return part.category.category;
}
